"use client";

import { useState } from "react";
import BlurFadeText from "@/components/common/blurFadeText";
import SectionEyebrow from "@/components/common/sectionEyebrow";
import SectionTitle from "@/components/common/sectionTitle";

function FAQItem({ item, isOpen, onToggle, itemId }) {
  return (
    <article
      className={`overflow-hidden rounded-lg bg-black/3  transition-colors duration-300 hover:bg-black/10`}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={`${itemId}-panel`}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-6 px-8 pt-6 py-6 text-left sm:px-6 cursor-pointer"
      >
        <span className="font-raleway text-2xl font-medium leading-[1.05] tracking-[-0.05em] text-[#06040c] sm:text-2xlc">
          {item.question}
        </span>
        <span
          className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent text-white"
          aria-hidden="true"
        >
          <span className="absolute h-[1.5px] w-4 rounded-md bg-primary" />
          <span
            className={`absolute h-4 w-[1.5px] rounded-md bg-primary transition-transform duration-300 ${
              isOpen ? "scale-y-0" : "scale-y-100"
            }`}
          />
        </span>
      </button>

      <div
        id={`${itemId}-panel`}
        className={`grid transition-all duration-300 ease-out ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <p className="max-w-3xl px-8 pb-8 text-lg leading-tight text-[#5f6663] sm:px-9 sm:text-[1.05rem]">
            {item.answer}
          </p>
        </div>
      </div>
    </article>
  );
}

export default function FAQSection({
  title,
  description,
  items = [],
  lists = [],
  defaultOpenIndex = 0,
  defaultListIndex = 0,
  className = "",
  stacked = false,
}) {
  const faqLists = lists.length
    ? lists
        .filter((list) => Array.isArray(list.items) && list.items.length > 0)
        .map((list, index) => ({
          id: list.id ?? list.title ?? `group-${index}`,
          title: list.title ?? `Group ${index + 1}`,
          items: list.items,
        }))
    : items.length > 0
      ? [{ id: "all", title: "All questions", items }]
      : [];

  const initialListIndex = faqLists[defaultListIndex] ? defaultListIndex : 0;
  const [activeListIndex, setActiveListIndex] = useState(initialListIndex);
  const activeList = faqLists[activeListIndex] ?? faqLists[0];
  const activeItems = activeList?.items ?? [];
  const [openIndex, setOpenIndex] = useState(
    activeItems[defaultOpenIndex] ? defaultOpenIndex : -1,
  );

  const handleSelectList = (index) => {
    const selectedItems = faqLists[index]?.items ?? [];

    setActiveListIndex(index);
    setOpenIndex(selectedItems[defaultOpenIndex] ? defaultOpenIndex : -1);
  };

  return (
    <section className={`w-full px-4 sm:px-6 lg:px-8 py-28 ${className}`}>
      <div
        className={`mx-auto w-full max-w-328 ${
          stacked ? "space-y-14" : "grid gap-14 lg:grid-cols-3 lg:gap-20"
        }`}
      >
        <div
          className={`space-y-8 ${
            stacked ? "mx-auto max-w-4xl text-center" : "max-w-2xl"
          }`}
        >
          <div className="space-y-6">
            <SectionEyebrow
              text="Safety Management FAQs"
              className={stacked ? "justify-center pt-0" : ""}
            />
            <SectionTitle
              text={title}
              className="text-[3rem] leading-[0.95] tracking-[-0.06em] sm:text-[2.9rem] lg:text-[3.25rem]"
            />
            <p
              className={`font-raleway text-md leading-[1.35] text-[#7a807d] ${
                stacked ? "mx-auto max-w-3xl" : "max-w-2xl"
              }`}
            >
              <BlurFadeText text={description} delay={0.16} blur={8} y={16} />
            </p>

            {faqLists.length > 1 ? (
              <div
                className={`flex flex-wrap gap-3 ${
                  stacked ? "justify-center" : ""
                }`}
              >
                {faqLists.map((list, index) => {
                  const isActive = index === activeListIndex;

                  return (
                    <button
                      key={`faq-chip-${list.id}`}
                      type="button"
                      aria-pressed={isActive}
                      onClick={() => handleSelectList(index)}
                      className={`rounded-full border px-4 py-2 font-raleway text-sm font-medium leading-none tracking-[-0.03em] transition-colors duration-300 cursor-pointer ${
                        isActive
                          ? "border-[#06040c] bg-[#06040c] text-white"
                          : "border-black/10 bg-white text-[#5f6663] hover:border-[#06040c]/25 hover:text-[#06040c]"
                      }`}
                    >
                      {list.title}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>

        <div
          className={`space-y-4 ${
            stacked ? "mx-auto w-full max-w-5xl" : "lg:col-span-2"
          }`}
        >
          {activeItems.map((item, index) => {
            const itemId = `faq-${activeList?.id ?? "group"}-${item.id ?? index}`;

            return (
              <FAQItem
                key={itemId}
                item={item}
                itemId={itemId}
                isOpen={index === openIndex}
                onToggle={() =>
                  setOpenIndex((currentIndex) =>
                    currentIndex === index ? -1 : index,
                  )
                }
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}
